import { useMemo } from "react";
import { motion, type Variants } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { CATEGORIES } from "@/constants/categories";
import { useCategoryImages } from "@/hooks/useSiteContent";

const reveal: Variants = {
  hidden: { opacity: 0, y: 48 },
  show: { opacity: 1, y: 0, transition: { duration: 0.9, ease: [0.16, 1, 0.3, 1] } },
};

interface CardProps {
  img?: string;
  index: string;
  title: string;
  sub: string;
  to: string;
  testId: string;
}

const Card = ({ img, index, title, sub, to, testId }: CardProps) => (
  <motion.div variants={reveal}>
    <Link
      to={to}
      className="group relative border border-black/10 bg-white flex flex-col h-full"
      data-testid={testId}
    >
      <div className="img-frame relative aspect-[4/3] w-full overflow-hidden bg-[#f3f4f6]">
        {img ? (
          <img src={img} alt={title} className="absolute inset-0 w-full h-full object-cover" />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center px-4 text-center text-xs text-vg-gray">
            Image coming soon
          </div>
        )}
        <div className="absolute top-4 left-4 bg-white/90 backdrop-blur px-3 py-1.5 font-mono-acc text-xs uppercase tracking-[0.2em]">
          {index}
        </div>
      </div>
      <div className="p-5 flex items-end justify-between gap-4 border-t border-black/10 shrink-0">
        <div className="min-w-0">
          <h3 className="font-heading font-black text-xl md:text-2xl tracking-tighter">{title}</h3>
          <p className="text-sm text-vg-gray mt-1 line-clamp-2">{sub}</p>
        </div>
        <span className="w-10 h-10 shrink-0 border border-black/15 flex items-center justify-center transition-all duration-300 group-hover:bg-vg-cyan group-hover:border-vg-cyan">
          <ArrowUpRight size={18} />
        </span>
      </div>
    </Link>
  </motion.div>
);

export const Categories = () => {
  const { data: categoryImages } = useCategoryImages();

  const categoryImageMap = useMemo(() => {
    const map = new Map<string, string>();
    for (const item of categoryImages ?? []) {
      if (item.image_url) map.set(item.slug, item.image_url);
    }
    return map;
  }, [categoryImages]);

  return (
    <section id="categories" className="pt-12 lg:pt-16 pb-24 lg:pb-32" data-testid="categories-section">
      <div className="max-w-[1600px] mx-auto px-6 lg:px-12">
        <motion.div
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-16"
        >
          <div>
            <span className="font-mono-acc uppercase tracking-[0.2em] text-xs text-vg-gray">01 / The Aisles</span>
            <h2 className="font-heading font-black tracking-tighter text-5xl md:text-7xl mt-4">
              SHOP BY<br />
              <span className="text-outline">CATEGORY</span>
            </h2>
          </div>
          <div className="flex flex-col gap-4 max-w-sm">
            <p className="text-vg-gray leading-relaxed text-base md:text-lg">
              Quality you can trust at prices that make sense.
            </p>
            <Link
              to="/products"
              className="btn-invert bg-vg-navy text-white px-5 py-2.5 font-mono-acc uppercase tracking-[0.2em] text-xs hover:bg-vg-cyan hover:text-white self-start"
              data-testid="categories-view-all"
            >
              View all products →
            </Link>
          </div>
        </motion.div>

        <motion.div
          variants={{ hidden: {}, show: { transition: { staggerChildren: 0.08 } } }}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-100px" }}
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8"
        >
          {CATEGORIES.map((category, i) => (
            <Card
              key={category.slug}
              img={categoryImageMap.get(category.slug)}
              index={`A/${String(i + 1).padStart(2, "0")}`}
              title={category.name.toUpperCase()}
              sub={category.description}
              to={`/products/${category.slug}`}
              testId={`category-card-${category.slug}`}
            />
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default Categories;
